// @ts-nocheck
import axios, { AxiosRequestConfig, Method } from "axios";
import Storage from "classes/Storage/Storage";
import { API_URL } from "./paths";
import formatDate from "./formatDate";
import * as Sentry from "@sentry/react";
import promiseToast from "components/Toasts/PromiseToast";
import { redact, scrubUrl } from "./sentryScrub";

export interface RequestProps extends AxiosRequestConfig {
	forceUrl?: boolean;
	token?: string;
	skipReport?: boolean;
	toast?: boolean | object | string;
}

export default function genRequest<T = any>(
	url: string,
	data?: any,
	method?: Method | string,
	props?: RequestProps
): Promise<T> {
	// Guard: don't send requests with undefined/null/false in the URL path
	if (url && /\/(undefined|null|false)(\/|$)/.test(url)) {
		return Promise.reject(new Error(`Invalid URL parameter in: ${url}`));
	}
	const storage = new Storage(null, null, localStorage);
	const localStor = new Storage();
	const userToken = storage.getItem("token") as any;
	let token = userToken?.access_token;

	if (props?.token) token = props.token;
	
	let settings: AxiosRequestConfig = {
		url: props && props.forceUrl ? url : API_URL + url,
		headers: {
			"Content-Type": "application/json",
			Authorization: "Bearer " + token,
			...props?.headers,
		},
		data: data,
		method: (method ? method : "get") as Method,
		onUploadProgress: props?.onUploadProgress,
		onDownloadProgress: props?.onDownloadProgress,
		timeout: props?.timeout || 30000,
	};

	if (method === "get" && data) {
		settings.params = data;
		settings.data = undefined;
	}

	if (!data) {
		settings.data = undefined;
	}

	if (!method && data) settings.method = "post";

	let req = axios(settings).then(
		(resp) => {
			let respData = resp.data;
			// Unwrap paginated responses: { items: [...], total, page, limit } → [...]
			if (respData && typeof respData === "object" && !Array.isArray(respData) && Array.isArray(respData.items)) {
				respData = respData.items;
			}
			return respData as T;
		},
		(e) => {
			const status = e.response?.status;
			// A wrong password answers 401 too: that is a form error, not an expired session
			const isCredentialsCheck = /^auth\/(login|change-password)/.test(url);
			if (status === 401 && !isCredentialsCheck) {
				try {
					storage.removeItem("token");
					const pathname = window.location.pathname.split("/")[1];
					let accounts: any[] = [];
					try {
						accounts = localStor.getItem("Accounts") || [];
					} catch (_) {
						// Corrupted/unreadable encrypted storage — reset
						accounts = [];
					}
					if (Array.isArray(accounts)) {
						const accountsModified = accounts.map((a: any) => {
							if (a.employeeId === parseFloat(pathname)) {
								a.token = null;
								a.tokenRegeneration = true;
							}
							return a;
						});
						localStor.setItem("Accounts", accountsModified);
					}
					const pathSegments = window.location.pathname.split("/");
					const lastSegment = pathSegments[pathSegments.length - 1];
					const isNumber =
						!isNaN(lastSegment as any) && !isNaN(parseFloat(lastSegment));
					if (
						!window.location.pathname.startsWith("/login") &&
						!isNumber
					) {
						window.location.href = "/login";
					}
				} catch (_) {
					// Last resort — redirect to login without crashing
					if (!window.location.pathname.startsWith("/login")) {
						window.location.href = "/login";
					}
				}
			}
			// wrong password / expired reset link: an expected answer, and the
			// request body is a credential
			const isAuthAnswer = /^auth\//.test(url) && status >= 400 && status < 500;
			if (props?.skipReport !== true && !isAuthAnswer) {
				sendReport(e, {
					url,
					data,
					method,
					props,
				});
			}
			throw e;
		}
	);
	if (props?.toast)
		promiseToast(
			req,
			typeof props.toast !== "boolean" ? props.toast : null
		);
	return req;
}

const MASKED_BODY = "[Filtered: auth request]";

export function sendReport(error: any, datas?: any) {
	if (!datas) datas = {};
	let status = error?.response?.status;
	let date = formatDate();
	if (status === 404) return false;

	error.name = `${
		error?.name
	} - ${datas.method?.toUpperCase()} - ${status} - ${scrubUrl(datas.url).replace(
		API_URL,
		""
	)}`;

	// never attach credentials: body, headers and token overrides are masked,
	// and the body of an auth/ call is dropped altogether
	const isAuth = /^auth\//.test(String(datas.url).replace(API_URL, ""));
	const { props: reqProps, ...rest } = datas;
	let vals = {
		Title: `${datas.method?.toUpperCase()} Error - ${status} - ${datas.url.replace(
			API_URL,
			""
		)}`,
		Date: date,
		...redact(rest),
		url: scrubUrl(datas.url),
		data: isAuth ? MASKED_BODY : redact(datas.data),
		props: redact(reqProps),
		response: isAuth ? undefined : redact(error.response?.data),
	};

	Sentry.withScope((scope) => {
		let user = scope.getUser();
		scope.addAttachment({
			filename: date + "-report.json",
			data: { ...vals, user },
			contentType: "application/json",
		});
		Sentry.captureException(error);
	});
}